import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Check, X, RefreshCw, ChevronRight, ChevronDown, AlertCircle } from 'lucide-react';
import {
  BrioPageHeader,
  BrioFrame,
  BrioAnalyticsSurface,
  BrioTable,
  BrioTableWrapper,
  BrioBadge,
  BrioButton,
  BrioInput,
  BrioSelect,
  BrioEmptyState,
  BrioGrid,
  BrioKpiBlock,
  getTablePropsFromCode,
} from '../../components/BrioComponents';
import { supabase } from '../../supabaseClient';
import { useFreelancerHours } from '../../hooks/useFreelancerHours';
import { normalizeId } from '../../hooks/useSupabase';

const PRESENTATION_CODE = '1b';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function monthFirstDay(year, month) {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}
function monthLastDay(year, month) {
  const d = new Date(Date.UTC(year, month, 0));
  return d.toISOString().split('T')[0];
}

// Builds the list of users whose overlapping role-history row has
// logging_mode='freelancer' for (year, month). Single query against
// user_role_history.
async function fetchFreelancerUserIds(year, month) {
  const start = monthFirstDay(year, month);
  const end = monthLastDay(year, month);
  const { data, error } = await supabase
    .from('user_role_history')
    .select('user_id, effective_from, effective_to, logging_mode')
    .eq('logging_mode', 'freelancer')
    .lte('effective_from', end)
    .or(`effective_to.is.null,effective_to.gte.${start}`);
  if (error) throw error;
  const ids = new Set();
  for (const row of data || []) ids.add(normalizeId(row.user_id));
  return ids;
}

// Year-wide variant: any user whose freelancer role history overlaps any
// part of the calendar year.
async function fetchFreelancerUserIdsForYear(year) {
  const start = `${year}-01-01`;
  const end = `${year}-12-31`;
  const { data, error } = await supabase
    .from('user_role_history')
    .select('user_id, effective_from, effective_to, logging_mode')
    .eq('logging_mode', 'freelancer')
    .lte('effective_from', end)
    .or(`effective_to.is.null,effective_to.gte.${start}`);
  if (error) throw error;
  const ids = new Set();
  for (const row of data || []) ids.add(normalizeId(row.user_id));
  return ids;
}

export default function FreelancerManagementPage({
  currentUser,
  activeUsers = [],
  activeProjects = [],
}) {
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';

  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(() => today.getFullYear());
  const [month, setMonth] = useState(() => today.getMonth() + 1);
  const [view, setView] = useState('month'); // 'month' | 'year'

  const {
    rows,
    loading: rowsLoading,
    error: rowsError,
    snapshotMonth,
    snapshotYear,
    setContractedHours,
    approve,
    unapprove,
  } = useFreelancerHours(year, view === 'year' ? null : month);

  // Freelancer user IDs for the active window
  const [freelancerIds, setFreelancerIds] = useState(new Set());
  const [freelancerIdsLoading, setFreelancerIdsLoading] = useState(false);
  const [freelancerIdsError, setFreelancerIdsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setFreelancerIdsLoading(true);
    setFreelancerIdsError(null);
    const promise = view === 'year'
      ? fetchFreelancerUserIdsForYear(year)
      : fetchFreelancerUserIds(year, month);
    promise
      .then(ids => { if (!cancelled) setFreelancerIds(ids); })
      .catch(err => { if (!cancelled) setFreelancerIdsError(err.message || 'Failed to load freelancers'); })
      .finally(() => { if (!cancelled) setFreelancerIdsLoading(false); });
    return () => { cancelled = true; };
  }, [year, month, view]);

  // Month view: one row per freelancer for the selected month.
  const tableRows = useMemo(() => {
    if (view !== 'month') return [];
    const rowByUserId = new Map();
    for (const r of rows) rowByUserId.set(normalizeId(r.userId), r);

    const list = [];
    for (const uid of freelancerIds) {
      const user = activeUsers.find(u => normalizeId(u.id) === uid);
      if (!user) continue;
      const row = rowByUserId.get(uid);
      list.push({
        userId: uid,
        name: user.name,
        email: user.email,
        officeLocation: user.officeLocation || 'NL',
        snapshotExists: !!row,
        loggedHours: row?.loggedHours ?? null,
        contractedHours: row?.contractedHours ?? null,
        status: row?.status || 'not_snapshotted',
        approvedAt: row?.approvedAt || null,
        approvedBy: row?.approvedBy || null,
        notes: row?.notes || '',
      });
    }
    return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [view, rows, freelancerIds, activeUsers]);

  // Year view: rows = freelancer, months[1..12] = freelancer_monthly_hours
  // entry (or null if not snapshotted). One pass over the year-wide rows.
  const yearGrid = useMemo(() => {
    if (view !== 'year') return [];
    const byUser = new Map(); // userId -> { months: {1: row, ...} }
    for (const r of rows) {
      const uid = normalizeId(r.userId);
      const m = r.month ? parseInt(String(r.month).slice(5, 7), 10) : null;
      if (!m) continue;
      if (!byUser.has(uid)) byUser.set(uid, { months: {} });
      byUser.get(uid).months[m] = r;
    }
    const list = [];
    for (const uid of freelancerIds) {
      const user = activeUsers.find(u => normalizeId(u.id) === uid);
      if (!user) continue;
      const months = (byUser.get(uid) || { months: {} }).months;
      let approvedTotal = 0;
      let approvedCount = 0;
      let pendingCount = 0;
      for (let m = 1; m <= 12; m++) {
        const row = months[m];
        if (!row) continue;
        if (row.status === 'approved') {
          approvedCount++;
          approvedTotal += Number(row.contractedHours || 0);
        } else if (row.status === 'pending') {
          pendingCount++;
        }
      }
      list.push({
        userId: uid,
        name: user.name,
        officeLocation: user.officeLocation || 'NL',
        months,
        approvedTotal,
        approvedCount,
        pendingCount,
      });
    }
    return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [view, rows, freelancerIds, activeUsers]);

  // KPI counts — month view counts users; year view counts (user × month) cells
  const kpis = useMemo(() => {
    if (view === 'year') {
      let pending = 0, approved = 0, notSnapshotted = 0;
      for (const u of yearGrid) {
        for (let m = 1; m <= 12; m++) {
          const r = u.months[m];
          if (!r) notSnapshotted++;
          else if (r.status === 'approved') approved++;
          else if (r.status === 'pending') pending++;
          else notSnapshotted++;
        }
      }
      return { total: yearGrid.length, pending, approved, notSnapshotted };
    }
    let pending = 0, approved = 0, notSnapshotted = 0;
    for (const r of tableRows) {
      if (r.status === 'approved') approved++;
      else if (r.status === 'pending') pending++;
      else notSnapshotted++;
    }
    return { total: tableRows.length, pending, approved, notSnapshotted };
  }, [view, tableRows, yearGrid]);

  // Per-row local edits to contracted hours (pending state)
  const [editValues, setEditValues] = useState({}); // { userId: hoursString }
  const [actionBusyUserId, setActionBusyUserId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [snapshotBusy, setSnapshotBusy] = useState(false);

  useEffect(() => { setEditValues({}); }, [year, month]);

  const handleSnapshot = useCallback(async () => {
    if (!isAdmin) return;
    setSnapshotBusy(true);
    setActionError(null);
    const result = view === 'year' ? await snapshotYear() : await snapshotMonth();
    setSnapshotBusy(false);
    if (!result.success) setActionError(result.error || 'Snapshot failed.');
  }, [isAdmin, view, snapshotMonth, snapshotYear]);

  // Year grid: one-click approve uses the logged hours value. For custom
  // hours admin should use Month view.
  const [cellBusyKey, setCellBusyKey] = useState(null);
  const handleCellApprove = useCallback(async (userId, m) => {
    if (!isAdmin) return;
    setCellBusyKey(`${userId}|${m}`);
    setActionError(null);
    const result = await approve(userId, null, null, m);
    setCellBusyKey(null);
    if (!result.success) setActionError(result.error || 'Approve failed.');
  }, [isAdmin, approve]);

  const handleCellUnapprove = useCallback(async (userId, m) => {
    if (!isAdmin) return;
    setCellBusyKey(`${userId}|${m}`);
    setActionError(null);
    const result = await unapprove(userId, m);
    setCellBusyKey(null);
    if (!result.success) setActionError(result.error || 'Unapprove failed.');
  }, [isAdmin, unapprove]);

  const handleApprove = useCallback(async (row) => {
    if (!isAdmin) return;
    setActionBusyUserId(row.userId);
    setActionError(null);
    const editedRaw = editValues[row.userId];
    let hours = null;
    if (editedRaw != null && editedRaw !== '') {
      const parsed = Number(editedRaw);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setActionBusyUserId(null);
        setActionError(`Invalid hours value for ${row.name}.`);
        return;
      }
      hours = parsed;
    } else if (row.contractedHours != null) {
      hours = row.contractedHours;
    } else if (row.loggedHours != null) {
      hours = row.loggedHours;
    }
    const result = await approve(row.userId, hours, null);
    setActionBusyUserId(null);
    if (!result.success) setActionError(result.error || 'Approve failed.');
    else setEditValues(v => { const { [row.userId]: _drop, ...rest } = v; return rest; });
  }, [isAdmin, approve, editValues]);

  const handleUnapprove = useCallback(async (row) => {
    if (!isAdmin) return;
    setActionBusyUserId(row.userId);
    setActionError(null);
    const result = await unapprove(row.userId);
    setActionBusyUserId(null);
    if (!result.success) setActionError(result.error || 'Unapprove failed.');
  }, [isAdmin, unapprove]);

  const handleSaveEditedHours = useCallback(async (row) => {
    if (!isAdmin) return;
    const editedRaw = editValues[row.userId];
    if (editedRaw == null || editedRaw === '') return;
    const parsed = Number(editedRaw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setActionError(`Invalid hours value for ${row.name}.`);
      return;
    }
    setActionBusyUserId(row.userId);
    setActionError(null);
    const result = await setContractedHours(row.userId, parsed);
    setActionBusyUserId(null);
    if (!result.success) setActionError(result.error || 'Save failed.');
    else setEditValues(v => { const { [row.userId]: _drop, ...rest } = v; return rest; });
  }, [isAdmin, setContractedHours, editValues]);

  // ── Expanded row: time-entry breakdown for the freelancer in this month
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const toggleExpanded = useCallback(async (uid) => {
    if (expandedUserId === uid) {
      setExpandedUserId(null);
      setBreakdown(null);
      return;
    }
    setExpandedUserId(uid);
    setBreakdown(null);
    setBreakdownLoading(true);
    try {
      const start = monthFirstDay(year, month);
      const end = monthLastDay(year, month);
      const { data, error } = await supabase
        .from('time_entries')
        .select('id, date, duration, description, project_id, approved')
        .eq('user_id', uid)
        .gte('date', start)
        .lte('date', end)
        .is('deleted_at', null)
        .order('date', { ascending: true });
      if (error) throw error;
      const projMap = new Map((activeProjects || []).map(p => [normalizeId(p.id), p]));
      const byProject = new Map();
      for (const e of data || []) {
        const pid = normalizeId(e.project_id);
        if (!byProject.has(pid)) byProject.set(pid, { project: projMap.get(pid), hours: 0, entries: [] });
        const bucket = byProject.get(pid);
        bucket.hours += (e.duration || 0) / 3600;
        bucket.entries.push(e);
      }
      const arr = Array.from(byProject.values()).sort((a, b) => b.hours - a.hours);
      setBreakdown({ projects: arr, total: arr.reduce((s, b) => s + b.hours, 0) });
    } catch (err) {
      setActionError(err.message || 'Failed to load time entries.');
    } finally {
      setBreakdownLoading(false);
    }
  }, [expandedUserId, year, month, activeProjects]);

  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const yearOptions = useMemo(() => {
    const ys = [];
    for (let y = 2018; y <= today.getFullYear() + 1; y++) ys.push(y);
    return ys;
  }, [today]);

  return (
    <>
      <BrioPageHeader
        title=""
        subtitle={`Snapshot, review and approve monthly contracted hours for freelancers. Approved hours are sourced by salary cost, project profitability and project cost ledger.`}
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* View toggle */}
            <div style={{
              display: 'inline-flex', borderRadius: '6px',
              border: '1px solid var(--brio-border, #d1d5db)', overflow: 'hidden',
              fontSize: '12px', flexShrink: 0,
            }}>
              {['month','year'].map(v => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  style={{
                    padding: '6px 14px',
                    background: view === v ? 'var(--brio-gold, #9F902A)' : '#fff',
                    color: view === v ? '#fff' : 'var(--brio-text, #475569)',
                    border: 'none', cursor: 'pointer',
                    textTransform: 'capitalize', fontWeight: view === v ? 600 : 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
            {view === 'month' && (
              <BrioSelect
                value={String(month)}
                onChange={e => setMonth(parseInt(e.target.value, 10))}
              >
                {MONTH_NAMES.map((n, i) => (
                  <option key={i + 1} value={String(i + 1)}>{n}</option>
                ))}
              </BrioSelect>
            )}
            <BrioSelect
              value={String(year)}
              onChange={e => setYear(parseInt(e.target.value, 10))}
            >
              {yearOptions.map(y => <option key={y} value={String(y)}>{y}</option>)}
            </BrioSelect>
            {isAdmin && (
              <BrioButton onClick={handleSnapshot} variant="primary" disabled={snapshotBusy || rowsLoading}>
                <RefreshCw size={14} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
                {snapshotBusy
                  ? (view === 'year' ? 'Refreshing year…' : 'Refreshing…')
                  : (view === 'year' ? 'Refresh year' : 'Refresh')}
              </BrioButton>
            )}
          </div>
        }
      />

      <BrioFrame>
        <BrioAnalyticsSurface>
          {/* KPIs */}
          <BrioGrid cols={4} gap="s">
            <BrioKpiBlock
              label={view === 'year' ? `Freelancers (${year})` : 'Freelancers (this month)'}
              value={String(kpis.total)}
            />
            <BrioKpiBlock label="Not snapshotted" value={String(kpis.notSnapshotted)} />
            <BrioKpiBlock label="Pending approval" value={String(kpis.pending)} />
            <BrioKpiBlock label="Approved" value={String(kpis.approved)} />
          </BrioGrid>

          {/* Errors */}
          {(rowsError || freelancerIdsError || actionError) && (
            <div style={{
              marginTop: '12px', padding: '10px 12px',
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
              color: '#991b1b', fontSize: '13px',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <AlertCircle size={16} />
              {rowsError || freelancerIdsError || actionError}
            </div>
          )}

          {/* Loading / Empty */}
          {(rowsLoading || freelancerIdsLoading) && (
            <div style={{ marginTop: '16px', color: 'var(--brio-muted)', fontSize: '13px' }}>
              {view === 'year'
                ? `Loading freelancers for ${year}…`
                : `Loading freelancers for ${MONTH_NAMES[month - 1]} ${year}…`}
            </div>
          )}

          {!rowsLoading && !freelancerIdsLoading && view === 'month' && tableRows.length === 0 && (
            <div style={{ marginTop: '16px' }}>
              <BrioEmptyState>
                No freelancers configured for {MONTH_NAMES[month - 1]} {year}.<br />
                Mark a team member's role history entry as "Freelancer" to add them here.
              </BrioEmptyState>
            </div>
          )}

          {!rowsLoading && !freelancerIdsLoading && view === 'year' && yearGrid.length === 0 && (
            <div style={{ marginTop: '16px' }}>
              <BrioEmptyState>
                No freelancers configured for {year}.<br />
                Mark a team member's role history entry as "Freelancer" to add them here.
              </BrioEmptyState>
            </div>
          )}

          {/* Year view grid */}
          {!rowsLoading && !freelancerIdsLoading && view === 'year' && yearGrid.length > 0 && (
            <div style={{ marginTop: '16px', overflowX: 'auto' }}>
              <table style={{
                width: '100%', borderCollapse: 'separate', borderSpacing: 0,
                fontSize: '12px', tableLayout: 'auto',
              }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px 10px', position: 'sticky', left: 0, background: '#fff', zIndex: 2, borderBottom: '1px solid var(--brio-border, #e2e8f0)' }}>
                      Name
                    </th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--brio-border, #e2e8f0)' }}>Office</th>
                    {MONTH_NAMES.map((n, i) => (
                      <th key={i} style={{ textAlign: 'center', padding: '6px 8px', minWidth: '64px', borderBottom: '1px solid var(--brio-border, #e2e8f0)' }}>
                        {n.slice(0, 3)}
                      </th>
                    ))}
                    <th style={{ textAlign: 'right', padding: '6px 10px', borderBottom: '1px solid var(--brio-border, #e2e8f0)' }}>
                      Approved total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {yearGrid.map(u => (
                    <tr key={u.userId}>
                      <td style={{ padding: '6px 10px', fontWeight: 500, position: 'sticky', left: 0, background: '#fff', zIndex: 1, borderBottom: '1px solid var(--brio-border, #f1f5f9)', whiteSpace: 'nowrap' }}>
                        {u.name}
                      </td>
                      <td style={{ padding: '6px 10px', color: 'var(--brio-muted)', borderBottom: '1px solid var(--brio-border, #f1f5f9)' }}>
                        {u.officeLocation}
                      </td>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                        const row = u.months[m];
                        const cellKey = `${u.userId}|${m}`;
                        const busy = cellBusyKey === cellKey;
                        const status = row?.status || 'not_snapshotted';
                        const palette = {
                          approved:        { bg: '#dcfce7', border: '#86efac', fg: '#166534' },
                          pending:         { bg: '#fef3c7', border: '#fde68a', fg: '#92400e' },
                          not_snapshotted: { bg: '#f1f5f9', border: '#e2e8f0', fg: '#94a3b8' },
                        }[status];
                        return (
                          <td
                            key={m}
                            title={
                              row
                                ? `Logged: ${row.loggedHours?.toFixed(2) ?? '—'}h · ` +
                                  `Contracted: ${row.contractedHours != null ? row.contractedHours.toFixed(2) + 'h' : '—'} · ` +
                                  `Status: ${status}`
                                : 'Not snapshotted'
                            }
                            style={{
                              padding: '4px',
                              textAlign: 'center',
                              borderBottom: '1px solid var(--brio-border, #f1f5f9)',
                            }}
                          >
                            <div style={{
                              display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                              padding: '4px 6px', borderRadius: '6px', minWidth: '54px',
                              background: palette.bg, border: `1px solid ${palette.border}`,
                              color: palette.fg, lineHeight: 1.2,
                              opacity: busy ? 0.5 : 1,
                            }}>
                              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: status === 'approved' ? 600 : 500 }}>
                                {row?.contractedHours != null
                                  ? row.contractedHours.toFixed(0)
                                  : (row?.loggedHours != null ? row.loggedHours.toFixed(0) : '—')}
                              </span>
                              {row?.loggedHours != null && (
                                <span style={{ fontSize: '10px', color: palette.fg, opacity: 0.75 }}>
                                  log {row.loggedHours.toFixed(0)}
                                </span>
                              )}
                              {isAdmin && status === 'pending' && (
                                <button
                                  onClick={() => handleCellApprove(u.userId, m)}
                                  disabled={busy}
                                  style={{
                                    marginTop: '3px', fontSize: '10px', padding: '1px 6px',
                                    background: '#fff', border: `1px solid ${palette.border}`,
                                    borderRadius: '4px', cursor: 'pointer', color: palette.fg,
                                  }}
                                  title="Approve using logged hours"
                                >
                                  Approve
                                </button>
                              )}
                              {isAdmin && status === 'approved' && (
                                <button
                                  onClick={() => handleCellUnapprove(u.userId, m)}
                                  disabled={busy}
                                  style={{
                                    marginTop: '3px', fontSize: '10px', padding: '1px 6px',
                                    background: '#fff', border: `1px solid ${palette.border}`,
                                    borderRadius: '4px', cursor: 'pointer', color: palette.fg,
                                  }}
                                  title="Unapprove and revert to pending"
                                >
                                  Unapprove
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td style={{
                        padding: '6px 10px', textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                        borderBottom: '1px solid var(--brio-border, #f1f5f9)',
                      }}>
                        {u.approvedTotal.toFixed(0)}h
                        <div style={{ fontSize: '10px', color: 'var(--brio-muted)', fontWeight: 400 }}>
                          {u.approvedCount} approved · {u.pendingCount} pending
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--brio-muted)' }}>
                Cells show contracted hours (large) and logged hours (small). One-click approval uses the logged hours value. To override the contracted amount before approving, switch to Month view.
              </div>
            </div>
          )}

          {/* Month view table */}
          {!rowsLoading && !freelancerIdsLoading && view === 'month' && tableRows.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <BrioTableWrapper>
                <BrioTable {...tableProps}>
                  <thead>
                    <tr>
                      <th style={{ width: '24px' }}></th>
                      <th>Name</th>
                      <th>Office</th>
                      <th style={{ textAlign: 'right' }}>Logged</th>
                      <th style={{ textAlign: 'right' }}>Contracted</th>
                      <th>Status</th>
                      <th>Approved</th>
                      {isAdmin && <th style={{ textAlign: 'right' }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map(row => {
                      const isExpanded = expandedUserId === row.userId;
                      const isBusy = actionBusyUserId === row.userId;
                      const editedValue = editValues[row.userId];
                      const inputValue = editedValue != null
                        ? editedValue
                        : (row.contractedHours != null
                            ? String(row.contractedHours)
                            : (row.loggedHours != null ? String(row.loggedHours) : ''));
                      return (
                        <React.Fragment key={row.userId}>
                          <tr>
                            <td>
                              {row.snapshotExists && (
                                <button
                                  onClick={() => toggleExpanded(row.userId)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--brio-muted)' }}
                                  title="View time entry breakdown"
                                >
                                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </button>
                              )}
                            </td>
                            <td style={{ fontWeight: 500 }}>{row.name}</td>
                            <td>{row.officeLocation}</td>
                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {row.loggedHours != null ? row.loggedHours.toFixed(2) : '—'}
                            </td>
                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {isAdmin && (row.status === 'pending' || row.status === 'not_snapshotted') ? (
                                <BrioInput
                                  type="number"
                                  min="0"
                                  step="0.25"
                                  value={inputValue}
                                  onChange={e => setEditValues(v => ({ ...v, [row.userId]: e.target.value }))}
                                  style={{ width: '90px', textAlign: 'right' }}
                                  disabled={isBusy}
                                />
                              ) : (
                                row.contractedHours != null ? row.contractedHours.toFixed(2) : '—'
                              )}
                            </td>
                            <td>
                              {row.status === 'approved' && <BrioBadge variant="approved">Approved</BrioBadge>}
                              {row.status === 'pending' && <BrioBadge variant="pending">Pending</BrioBadge>}
                              {row.status === 'not_snapshotted' && <BrioBadge variant="rejected">Not snapshotted</BrioBadge>}
                            </td>
                            <td style={{ fontSize: '12px', color: 'var(--brio-muted)' }}>
                              {row.approvedAt
                                ? new Date(row.approvedAt).toLocaleDateString()
                                : '—'}
                            </td>
                            {isAdmin && (
                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {row.status === 'approved' ? (
                                  <BrioButton variant="secondary" onClick={() => handleUnapprove(row)} disabled={isBusy}>
                                    <X size={12} style={{ marginRight: '4px', verticalAlign: '-2px' }} />
                                    Unapprove
                                  </BrioButton>
                                ) : (
                                  <>
                                    {editedValue != null && editedValue !== '' && row.snapshotExists && (
                                      <BrioButton variant="secondary" onClick={() => handleSaveEditedHours(row)} disabled={isBusy} className="brio-mr-xs">
                                        Save
                                      </BrioButton>
                                    )}
                                    <BrioButton variant="primary" onClick={() => handleApprove(row)} disabled={isBusy}>
                                      <Check size={12} style={{ marginRight: '4px', verticalAlign: '-2px' }} />
                                      Approve
                                    </BrioButton>
                                  </>
                                )}
                              </td>
                            )}
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={isAdmin ? 8 : 7} style={{ background: '#fafaf6', padding: '12px 16px' }}>
                                {breakdownLoading && <div style={{ fontSize: '12px', color: 'var(--brio-muted)' }}>Loading time entries…</div>}
                                {!breakdownLoading && breakdown && (
                                  <div>
                                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--brio-text)' }}>
                                      Project breakdown — total {breakdown.total.toFixed(2)}h
                                    </div>
                                    {breakdown.projects.length === 0 ? (
                                      <div style={{ fontSize: '12px', color: 'var(--brio-muted)', fontStyle: 'italic' }}>
                                        No time entries this month.
                                      </div>
                                    ) : (
                                      <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                        <thead>
                                          <tr style={{ textAlign: 'left', color: 'var(--brio-muted)' }}>
                                            <th style={{ padding: '4px 8px' }}>Project</th>
                                            <th style={{ padding: '4px 8px', textAlign: 'right' }}>Hours</th>
                                            <th style={{ padding: '4px 8px', textAlign: 'right' }}>Share</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {breakdown.projects.map((b, i) => (
                                            <tr key={i}>
                                              <td style={{ padding: '4px 8px' }}>
                                                {b.project?.name || <em>Unassigned</em>}
                                                {b.project?.code && <span style={{ color: 'var(--brio-muted)', marginLeft: '6px' }}>{b.project.code}</span>}
                                              </td>
                                              <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                {b.hours.toFixed(2)}
                                              </td>
                                              <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--brio-muted)' }}>
                                                {breakdown.total > 0 ? ((b.hours / breakdown.total) * 100).toFixed(1) + '%' : '—'}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </BrioTable>
              </BrioTableWrapper>
            </div>
          )}

          {!isAdmin && tableRows.length > 0 && (
            <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--brio-muted)' }}>
              You have read-only access to this page.
            </div>
          )}

          <div style={{ marginTop: '16px', fontSize: '12px', color: 'var(--brio-muted)' }}>
            Tip: approved monthly hours override the role-history value for salary cost,
            project profitability and project cost ledger. Months not yet approved fall
            back to the freelancer's logged hours.
          </div>
        </BrioAnalyticsSurface>
      </BrioFrame>
    </>
  );
}
